import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PAGE_LABELS, type PageKey } from "@owners/shared-types";
import { moneyCompact, num, pct, pts } from "@owners/format";
import { api, useMe } from "../api";
import { PATHS } from "../routes";
import { Tile } from "../components/Numbers";

/**
 * Asset Management (demo artboard): headline cards + "every section at a glance".
 * Only sections this level can open (DECISIONS). Status dots come from data the
 * app already serves — /api/today/numbers and /api/nav-counts — nothing new is
 * computed here. Sections with nothing to judge get a grey dot and a description.
 */

type Dot = "good" | "watch" | "bad" | "none";
interface Glance { dot: Dot; line: string }

interface Numbers {
  yesterday: null | { occupancy: number | null; lastYear: null | { occupancy: number | null } };
  noiLastMonth: null | { label: string; actual: number | null; budget: number | null };
  noiFullYearReforecast: null | { projected: number; budget: number | null; year: number };
  q4Distribution: null | { available: number | null; contributionsNeeded?: number };
  loansBelowCovenant: null | { count: number; debt: number; watch: number; loans: number };
}
type Counts = Partial<Record<PageKey, { n: number; label: string }>>;

const GLANCE_PAGES: PageKey[] = [
  "flash_reports", "financial_reports", "awaiting_approval", "missing_data", "reo_documents", "compliance",
  "debt_compliance", "budget_reforecast", "my_reports", "trends", "team_members", "leadership",
  "guest_reviews", "receiver_reports", "receivership", "request_records",
];

const BLURB: Partial<Record<PageKey, string>> = {
  awaiting_approval: "Spend and requests waiting on you",
  my_reports: "Build and re-download report packages",
  trends: "Your hotels against the economy and market",
  team_members: "People, training and open roles",
  leadership: "Hotel leaders and their tenure",
  guest_reviews: "Review scores and recent comments",
  receiver_reports: "Monthly receiver reporting",
  receivership: "Receivership inputs",
  request_records: "Ask for tax and closing records",
  financial_reports: "Monthly P&L against budget and last year",
  flash_reports: "Nightly occupancy, ADR and RevPAR",
};

export function glanceFor(p: PageKey, n: Numbers | undefined, c: Counts | undefined): Glance {
  const count = c?.[p];
  if (p === "reo_documents" || p === "compliance" || p === "missing_data") {
    if (!c) return { dot: "none", line: "…" };
    return count ? { dot: p === "compliance" ? "bad" : "watch", line: count.label } : { dot: "good", line: "Everything on file" };
  }
  if (p === "flash_reports" && n?.yesterday?.occupancy != null) {
    const ly = n.yesterday.lastYear?.occupancy;
    if (ly == null) return { dot: "none", line: `Occupancy yesterday ${pct(n.yesterday.occupancy, { ratio: true })}` };
    const d = (n.yesterday.occupancy - ly) * 100;
    return { dot: d >= 0 ? "good" : d > -3 ? "watch" : "bad", line: `Occupancy ${pts(d)} vs last year` };
  }
  if (p === "financial_reports" && n?.noiLastMonth?.actual != null && n.noiLastMonth.budget != null) {
    const d = n.noiLastMonth.actual - n.noiLastMonth.budget;
    return { dot: d >= 0 ? "good" : "bad", line: `${n.noiLastMonth.label} NOI ${moneyCompact(Math.abs(d))} ${d >= 0 ? "over" : "under"} budget` };
  }
  if (p === "debt_compliance" && n?.loansBelowCovenant) {
    const l = n.loansBelowCovenant;
    if (!l.loans) return { dot: "none", line: "No loans entered yet" };
    return l.count ? { dot: "bad", line: `${num(l.count)} below covenant` }
      : l.watch ? { dot: "watch", line: `All pass · ${num(l.watch)} on watch` } : { dot: "good", line: `All ${num(l.loans)} loans pass` };
  }
  if (p === "budget_reforecast" && n?.noiFullYearReforecast?.budget != null) {
    const f = n.noiFullYearReforecast; const d = f.projected - f.budget!;
    return { dot: d >= 0 ? "good" : "watch", line: `${f.year} NOI reforecast ${moneyCompact(Math.abs(d))} ${d >= 0 ? "ahead of" : "behind"} budget` };
  }
  return { dot: "none", line: BLURB[p] ?? "Open" };
}

export function AssetManagement() {
  const { data: me } = useMe();
  const n = useQuery({ queryKey: ["today-numbers"], queryFn: () => api<Numbers>("/api/today/numbers") });
  const c = useQuery({ queryKey: ["nav-counts"], queryFn: () => api<{ counts: Counts }>("/api/nav-counts"), staleTime: 300_000 });
  const can = (p: PageKey) => !!me?.pages.includes(p);
  const sections = GLANCE_PAGES.filter(can);
  const d = n.data;
  const l = d?.loansBelowCovenant;
  const fy = d?.noiFullYearReforecast;
  const docs = (["reo_documents", "compliance", "missing_data"] as PageKey[]).filter(can).reduce((t, p) => t + (c.data?.counts[p]?.n ?? 0), 0);

  return (
    <>
      <div className="eyebrow">Owners Module · Asset Management</div>
      <h1>Every section at a glance</h1>

      <div className="tiles strip">
        <Tile label={`NOI reforecast${fy ? ` · ${fy.year}` : ""}`} tbd={!n.isLoading && !fy}
          value={n.isLoading ? "…" : moneyCompact(fy?.projected)}
          sub={fy?.budget != null ? `${moneyCompact(fy.projected - fy.budget)} vs budget` : "Arrives with Budget + Reforecast"} />
        <Tile label="Debt risk" tbd={!n.isLoading && !l}
          value={n.isLoading ? "…" : l ? (l.count ? `${num(l.count)} below` : l.watch ? `${num(l.watch)} watch` : "Low") : "—"}
          sub={l ? (l.count ? `${moneyCompact(l.debt)} of debt below covenant` : `${num(l.loans)} loans tested`) : "Enter loans in Debt Compliance"} />
        <Tile label="Documents needing attention" value={c.isLoading ? "…" : num(docs)} sub="REO, Compliance and Missing Data" />
        <Tile label="Q4 distribution" tbd={!n.isLoading && d?.q4Distribution?.available == null}
          value={n.isLoading ? "…" : moneyCompact(d?.q4Distribution?.available)}
          sub={d?.q4Distribution?.contributionsNeeded ? `${num(d.q4Distribution.contributionsNeeded)} hotels need a contribution` : "After debt service, reserves and CapEx"} />
      </div>

      <div className="glance">
        {sections.map((p) => {
          const g = glanceFor(p, d, c.data?.counts);
          return (
            <Link key={p} to={PATHS[p]} className="card glance-card">
              <div className="glance-head"><span className={`dot dot-${g.dot}`} />{PAGE_LABELS[p]}</div>
              <div className="glance-line">{g.line}</div>
              <div className="glance-open">Open →</div>
            </Link>
          );
        })}
      </div>
    </>
  );
}
